import { useState } from 'react';
import { useStore } from '../../data/store';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import { MapPin, Train, Car, Bed, ShieldCheck, Ticket, ChevronDown, ChevronUp } from 'lucide-react';

const tabs = [
  { key: 'eintritt', label: 'EINTRITT', icon: Ticket },
  { key: 'anreise', label: 'ANREISE', icon: Car },
  { key: 'parken', label: 'PARKEN', icon: MapPin },
  { key: 'uebernachtung', label: 'ÜBERNACHTUNG', icon: Bed },
  { key: 'regeln', label: 'REGELN', icon: ShieldCheck },
];

export default function Info() {
  const event = useStore((s) => s.event);
  const [activeTab, setActiveTab] = useState('eintritt');
  const [ref, isVisible] = useScrollAnimation();
  const [cookieConsent, setCookieConsent] = useState(() => localStorage.getItem('rnr_cookies') === 'accepted');

  const renderContent = (tabKey) => {
    const info = event?.infoTabs || {};
    switch (tabKey || activeTab) {
      case 'eintritt':
        return (
          <div className="text-center">
            <div className="inline-block bg-accent-red text-white font-heading text-2xl md:text-3xl tracking-wider px-8 md:px-10 py-4 md:py-5 mb-6 md:mb-8">
              EINTRITT FREI
            </div>
            <p className="font-body text-text-muted leading-relaxed text-base md:text-lg max-w-2xl mx-auto">{info.eintritt}</p>
          </div>
        );
      case 'anreise':
        return (
          <div className="space-y-6 md:space-y-8">
            <div className="flex gap-4 items-start">
              <Car className="text-accent-red shrink-0 mt-1" size={24} />
              <div>
                <h4 className="font-heading text-xl text-text-primary mb-2">MIT DEM AUTO</h4>
                <p className="font-body text-text-muted leading-relaxed">{info.anreiseAuto}</p>
              </div>
            </div>
            <div className="flex gap-4 items-start">
              <Train className="text-accent-red shrink-0 mt-1" size={24} />
              <div>
                <h4 className="font-heading text-xl text-text-primary mb-2">MIT DER BAHN</h4>
                <p className="font-body text-text-muted leading-relaxed">{info.anreiseBahn}</p>
              </div>
            </div>
            {cookieConsent && (
              <div className="mt-6">
                <iframe
                  src={event?.mapsUrl || "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d10194!2d7.3!3d50.517!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47beb3e6c4b3d6af%3A0x4e58c06b1e5b7c5a!2sBad%20H%C3%B6nningen!5e0!3m2!1sde!2sde"}
                  width="100%"
                  height="300"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  title="Karte Bad Hönningen"
                  className="border border-divider"
                />
              </div>
            )}
            {!cookieConsent && (
              <div className="mt-6 bg-bg-card border border-divider p-6 text-center">
                <p className="text-text-muted font-body text-sm mb-4">Die Karte wird nach Akzeptierung der Cookies geladen.</p>
                <button
                  onClick={() => { localStorage.setItem('rnr_cookies', 'accepted'); setCookieConsent(true); }}
                  className="bg-accent-red text-white font-heading tracking-wider px-6 py-2.5 border-2 border-accent-red hover:bg-transparent hover:text-accent-red transition-all text-sm min-h-[44px]"
                >
                  COOKIES AKZEPTIEREN
                </button>
              </div>
            )}
          </div>
        );
      case 'parken':
        return <p className="font-body text-text-muted leading-relaxed text-base md:text-lg">{info.parken}</p>;
      case 'uebernachtung':
        return <p className="font-body text-text-muted leading-relaxed text-base md:text-lg whitespace-pre-line">{info.uebernachtung}</p>;
      case 'regeln':
        return (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
            {(info.regeln || '').split('\n').filter(Boolean).map((rule, i) => (
              <div key={i} className={`flex items-center gap-4 p-4 border border-divider ${rule.startsWith('✗') ? 'border-accent-red/30' : 'border-divider'}`}>
                <span className={`text-xl ${rule.startsWith('✗') ? 'text-accent-red' : 'text-green-500'}`}>
                  {rule.startsWith('✗') ? '✗' : '✓'}
                </span>
                <span className="font-body text-text-muted text-sm md:text-base">{rule.substring(2)}</span>
              </div>
            ))}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <section id="infos" className="bg-bg-primary section-padding min-h-[400px]">
      <div
        ref={ref}
        className={`max-w-5xl mx-auto transition-all duration-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        <div className="text-center mb-10 md:mb-16">
          <h2 className="font-heading text-text-primary" style={{ fontSize: 'clamp(28px, 7vw, 56px)' }}>INFOS & ANREISE</h2>
        </div>

        <div className="hidden md:flex flex-wrap justify-center gap-3 mb-12">
          {tabs.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`flex items-center gap-2 font-heading text-sm tracking-wider px-4 py-2.5 border-2 transition-all min-h-[44px] ${
                activeTab === key
                  ? 'bg-accent-red border-accent-red text-white'
                  : 'border-divider text-text-muted hover:border-accent-red hover:text-accent-red'
              }`}
            >
              <Icon size={16} />
              {label}
            </button>
          ))}
        </div>
        <div className="hidden md:block bg-bg-card p-8 md:p-12 border border-divider">{renderContent()}</div>

        <div className="md:hidden space-y-2">
          {tabs.map(({ key, label, icon: Icon }) => (
            <div key={key} className={`border-l-[3px] ${activeTab === key ? 'border-accent-red' : 'border-transparent'} bg-bg-card`}>
              <button
                onClick={() => setActiveTab(activeTab === key ? '' : key)}
                className="w-full flex items-center justify-between px-5 py-4 min-h-[52px] text-left"
                aria-expanded={activeTab === key}
                aria-controls={`panel-${key}`}
              >
                <span className="flex items-center gap-3">
                  <Icon size={18} className={activeTab === key ? 'text-accent-red' : 'text-text-muted'} />
                  <span className={`font-heading text-sm tracking-wider ${activeTab === key ? 'text-text-primary' : 'text-text-muted'}`}>{label}</span>
                </span>
                {activeTab === key ? <ChevronUp size={18} className="text-text-muted" /> : <ChevronDown size={18} className="text-text-muted" />}
              </button>
              {activeTab === key && (
                <div id={`panel-${key}`} role="region" className="px-5 pb-6 pt-2 border-t border-divider/50">
                  {renderContent(key)}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

    </section>
  );
}
